import * as React from "react";
import { gql, useMutation, ApolloError } from "@apollo/client";

import {
  UpdateBusinessInfoMutationMutation,
  UpdateBusinessInfoMutationMutationVariables
} from "../../../schemaTypes";

export const updateBusinessInfoMutation = gql`
  mutation UpdateBusinessInfoMutation(
    $input: UpdateBusinessInfoInput!
  ) {
    updateBusinessInfo(
      input: $input
    ) {
      errors {
        path
        message
      }
      message
      logoUrl
      bannerUrl
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: UpdateBusinessInfoMutationMutationVariables & {
          logoFile?: File;
          bannerFile?: File;
        }
      ) => Promise<{
        errors: ({ path: string; message: string } | null)[] | null;
        message?: string;
        logoUrl?: string;
        bannerUrl?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const UpdateBusinessInfo: React.FC<Props> = ({ children, onMessage }) => {
  const [updateBusinessInfo, { loading, error }] = useMutation<
    UpdateBusinessInfoMutationMutation,
    UpdateBusinessInfoMutationMutationVariables
  >(updateBusinessInfoMutation);

  const submit = async (
    values: UpdateBusinessInfoMutationMutationVariables & { logoFile?: File; bannerFile?: File }
  ) => {
    const { logoFile, bannerFile, ...rest } = values;
    let logoUrl = rest.input.logoUrl;
    let bannerUrl = rest.input.bannerUrl;
    if (logoFile) {
      logoUrl = await fileToBase64(logoFile);
    }
    if (bannerFile) {
      bannerUrl = await fileToBase64(bannerFile);
    }
    const { data } = await updateBusinessInfo({
      variables: {
        input: {
          ...rest.input,
          logoUrl,
          bannerUrl,
        },
      },
    });

    const errors = data?.updateBusinessInfo?.errors ?? null;
    const message = data?.updateBusinessInfo?.message ?? undefined;
    const logoUrlResp = data?.updateBusinessInfo?.logoUrl ?? undefined;
    const bannerUrlResp = data?.updateBusinessInfo?.bannerUrl ?? undefined;

    if (message && onMessage) {
      onMessage(message);
    }

    return {
      errors,
      logoUrl: logoUrlResp,
      bannerUrl: bannerUrlResp,
      message,
    };
  };

  return children({ submit, loading, error });
};
